<template>
  <el-dialog
    title="绑定物料"
    :visible.sync="dialogVisible"
    width="900px"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <!-- 搜索区域 -->
    <div class="search-bar">
      <el-input
        v-model="searchKeyword"
        placeholder="输入物料编码或名称搜索"
        prefix-icon="el-icon-search"
        clearable
        style="width: 300px"
        @keyup.enter.native="handleSearch"
      />
      <el-button type="primary" icon="el-icon-search" @click="handleSearch">搜索</el-button>
      <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
    </div>

    <!-- 物料列表 -->
    <el-table
      ref="materialTable"
      :data="materialList"
      v-loading="loading"
      border
      stripe
      height="400px"
      row-key="id"
      @select="handleSelect"
      @select-all="handleSelectAll"
    >
      <el-table-column type="selection" width="50" :reserve-selection="true" />
      <el-table-column prop="number" label="物料编码" width="150" />
      <el-table-column prop="name" label="物料名称" min-width="200" />
      <el-table-column prop="model" label="规格型号" width="120" />
      <el-table-column prop="registeredMark" label="品牌" width="100" />
      <el-table-column prop="shortName" label="简称" width="100" />
    </el-table>

    <!-- 分页 -->
    <el-pagination
      v-if="total > 0"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
      :current-page="currentPage"
      :page-sizes="[10, 20, 50, 100]"
      :page-size="pageSize"
      layout="total, sizes, prev, pager, next"
      :total="total"
      style="margin-top: 15px; text-align: right"
    />

    <!-- 已选物料信息 -->
    <div class="selected-section">
      <div class="selected-header">
        <span>已选择 <strong>{{ selectedCount }}</strong> 个物料</span>
        <el-button type="text" size="small" @click="clearSelection" v-if="selectedCount > 0">清空选择</el-button>
      </div>
      <div class="selected-tags" v-if="selectedCount > 0 && selectedCount <= 20">
        <el-tag
          v-for="item in selectedMaterialsArray"
          :key="item.id"
          closable
          size="small"
          @close="removeSelected(item)"
          style="margin: 2px"
        >
          {{ item.name }}
        </el-tag>
      </div>
      <div class="selected-tags" v-else-if="selectedCount > 20">
        <span class="hint-text">已选择超过20个物料，不再逐一显示</span>
      </div>
    </div>

    <div slot="footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleConfirm" :loading="submitLoading" :disabled="selectedCount === 0">
        确认绑定 ({{ selectedCount }})
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { queryPageListByAll } from '@/api/system/good'
import { bindMaterials } from '@/api/system/sale_category'

export default {
  name: 'MaterialBind',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    category: {
      type: Object,
      default: null
    },
    saleOrgId: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      searchKeyword: '',
      materialList: [],
      loading: false,
      currentPage: 1,
      pageSize: 10,
      total: 0,
      // 使用 Map 存储跨翻页选中的物料，key 为物料 id
      selectedMaterialsMap: new Map(),
      submitLoading: false
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    selectedCount() {
      return this.selectedMaterialsMap.size
    },
    selectedMaterialsArray() {
      return Array.from(this.selectedMaterialsMap.values())
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetData()
        this.loadMaterialList()
      }
    }
  },
  methods: {
    resetData() {
      this.searchKeyword = ''
      this.currentPage = 1
      this.selectedMaterialsMap = new Map()
    },

    async loadMaterialList() {
      this.loading = true
      try {
        const params = {
          current: this.currentPage,
          size: this.pageSize,
          name: this.searchKeyword,
          orgSales: [{ id: this.saleOrgId }]
        }
        const res = await queryPageListByAll(params)
        if (res.code === 200 && res.data) {
          this.materialList = res.data.records || []
          this.total = res.data.total || 0
          // 加载完成后，恢复当前页已选中的行
          this.$nextTick(() => {
            this.restoreSelection()
          })
        } else {
          this.materialList = []
          this.total = 0
        }
      } catch (error) {
        console.error('加载物料列表失败:', error)
        this.$message.error('加载物料列表失败')
        this.materialList = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },

    // 恢复当前页的选中状态
    restoreSelection() {
      if (!this.$refs.materialTable) return
      this.materialList.forEach(row => {
        if (this.selectedMaterialsMap.has(row.id)) {
          this.$refs.materialTable.toggleRowSelection(row, true)
        }
      })
    },

    // 单行选择/取消选择
    handleSelect(selection, row) {
      const isSelected = selection.some(item => item.id === row.id)
      if (isSelected) {
        this.selectedMaterialsMap.set(row.id, {
          id: row.id,
          number: row.number,
          name: row.name
        })
      } else {
        this.selectedMaterialsMap.delete(row.id)
      }
      // 触发响应式更新
      this.selectedMaterialsMap = new Map(this.selectedMaterialsMap)
    },

    // 全选/取消全选当前页
    handleSelectAll(selection) {
      if (selection.length > 0) {
        // 全选当前页
        selection.forEach(row => {
          this.selectedMaterialsMap.set(row.id, {
            id: row.id,
            number: row.number,
            name: row.name
          })
        })
      } else {
        // 取消全选当前页
        this.materialList.forEach(row => {
          this.selectedMaterialsMap.delete(row.id)
        })
      }
      // 触发响应式更新
      this.selectedMaterialsMap = new Map(this.selectedMaterialsMap)
    },

    // 移除单个已选物料
    removeSelected(item) {
      this.selectedMaterialsMap.delete(item.id)
      this.selectedMaterialsMap = new Map(this.selectedMaterialsMap)
      // 同步更新表格选中状态
      const row = this.materialList.find(m => m.id === item.id)
      if (row && this.$refs.materialTable) {
        this.$refs.materialTable.toggleRowSelection(row, false)
      }
    },

    // 清空所有选择
    clearSelection() {
      this.selectedMaterialsMap = new Map()
      if (this.$refs.materialTable) {
        this.$refs.materialTable.clearSelection()
      }
    },

    handleSearch() {
      this.currentPage = 1
      this.loadMaterialList()
    },

    handleReset() {
      this.searchKeyword = ''
      this.currentPage = 1
      this.loadMaterialList()
    },

    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1
      this.loadMaterialList()
    },

    handlePageChange(page) {
      this.currentPage = page
      this.loadMaterialList()
    },

    async handleConfirm() {
      if (this.selectedCount === 0) {
        this.$message.warning('请选择要绑定的物料')
        return
      }

      this.submitLoading = true
      try {
        // 构建符合后端接口的数据结构
        const bindData = {
          saleCategoryId: this.category.id,
          saleOrgId: this.saleOrgId,
          materialIds: Array.from(this.selectedMaterialsMap.keys())
        }

        const res = await bindMaterials(bindData)
        if (res.code === 200) {
          this.$message.success(`成功绑定 ${this.selectedCount} 个物料`)
          this.$emit('success')
          this.handleClose()
        } else {
          this.$message.error(res.msg || '绑定失败')
        }
      } catch (error) {
        console.error('绑定物料失败:', error)
        this.$message.error('绑定物料失败')
      } finally {
        this.submitLoading = false
      }
    },

    handleClose() {
      this.dialogVisible = false
      this.clearSelection()
    }
  }
}
</script>

<style scoped>
.search-bar {
  margin-bottom: 15px;
  display: flex;
  gap: 10px;
}

.selected-section {
  margin-top: 15px;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.selected-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.selected-header strong {
  color: #409eff;
  font-size: 16px;
}

.selected-tags {
  max-height: 100px;
  overflow-y: auto;
}

.hint-text {
  color: #909399;
  font-size: 13px;
}
</style>
